import React, { useEffect, useState } from "react";
import { Modal, ModalDialog, Button, Chip, CircularProgress, Tooltip } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import styles from "./staffmanage.module.css";
import { API, toastOptions, errorText, mmdd, mmddHHmm, openFileLink } from "./rosterutils";

//계약서 이력 — 그 직원(사람)의 모든 문서. 필터: 전체 / 각 직무(파트) / 영상동의서
//회차는 파트별로 셈 · 첫 계약(1회차) 첨부만 신분증·통장사본 버튼, 그 외는 "빈 파일 N건"
//[제외] = 잘못 보냈는데 서명까지 된 문서를 회차·집계에서 뺌(서버가 회차 다시 매김). 행은 회색 취소선으로 남고 [복구] 가능
const CANCEL_MESSAGE = "관리자에 의해 취소되었습니다";

const ContractHistoryModal: React.FC<any> = (props) => {

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>(null);
    const [filter, setFilter] = useState<"all" | "video" | number>("all");
    const [busyId, setBusyId] = useState<number | null>(null);

    useEffect(() => {
        if (!props.open || !props.userId) {
            return;
        }
        setFilter("all");
        setData(null);
        load();
    }, [props.open, props.userId]);

    const load = async () => {
        try {
            setLoading(true);
            const result: any = await getFetch(`${API}/users/${props.userId}/documents`);
            if (result.message === "success") {
                setData(result);
            } else {
                toast.error(errorText(result, "이력을 불러오지 못했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    const remind = async (doc: any) => {
        if (!window.confirm(`${props.userName}에게 서명 재알림을 보낼까요?${doc.expired ? " (유효기간이 지나 7일 연장 후 보냅니다)" : ""}`)) return;
        try {
            setBusyId(doc.id);
            const result: any = await postFetch(`${API}/documents/remind`, { id: doc.id });
            if (result.message === "success") {
                toast.success(`재알림 발송 (${result.remindCount}회${result.extended ? ", 유효기간 +7일" : ""})`, toastOptions);
                await load();
                props.onChanged && props.onChanged();
            } else {
                toast.error(errorText(result, "재알림에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setBusyId(null);
        }
    };

    const cancel = async (doc: any) => {
        if (!window.confirm(`이 문서의 서명 요청을 취소할까요?`)) return;
        try {
            setBusyId(doc.id);
            const result: any = await postFetch(`${API}/documents/cancel`, { id: doc.id, message: CANCEL_MESSAGE });
            if (result.message === "success") {
                toast.success("요청을 취소했습니다", toastOptions);
                await load();
                props.onChanged && props.onChanged();
            } else {
                toast.error(errorText(result, "취소에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setBusyId(null);
        }
    };

    //제외 / 복구 (서명완료 근로계약서만)
    const setExcluded = async (doc: any, excluded: boolean) => {
        let reason = "";
        if (excluded) {
            const input = window.prompt(`이 계약서(${doc.roundNo}회차 ${doc.periodStart || ""} ~ ${doc.periodEnd || ""})를 회차에서 제외할까요?\n제외하면 뒤 회차가 하나씩 당겨지고, 이 줄은 기록으로만 남습니다.\n\n사유 (선택):`, "");
            if (input === null) return;
            reason = input;
        } else if (!window.confirm("제외를 취소하고 다시 회차에 넣을까요?")) {
            return;
        }
        try {
            setBusyId(doc.id);
            const result: any = await postFetch(`${API}/documents/exclude`, { id: doc.id, excluded, reason });
            if (result.message === "success") {
                toast.success(excluded ? "회차에서 제외했습니다" : "복구했습니다", toastOptions);
                await load();
                props.onChanged && props.onChanged();
            } else {
                toast.error(errorText(result, excluded ? "제외에 실패했습니다" : "복구에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setBusyId(null);
        }
    };

    const positions: any[] = data ? data.positions || [] : [];
    const documents: any[] = data ? data.documents || [] : [];
    const filtered = documents.filter((d) => {
        if (filter === "all") return true;
        if (filter === "video") return d.kind === "videoConsent";
        return d.kind === "contract" && d.positionId === filter;
    });

    const renderStatus = (d: any) => {
        if (d.isExcluded) {
            return <span title={d.excludedReason || ""}><Chip size="sm" variant="soft" color="neutral">제외됨</Chip><span className={`${styles.subText} ${styles.noStrike}`} style={{ marginLeft: "6px" }}>{mmdd(d.excludedAt)} {d.excludedByName}{d.excludedReason ? ` · ${d.excludedReason}` : ""}</span></span>;
        }
        if (d.kind === "videoConsent") {
            const st = d.videoConsent ? d.videoConsent.status : null;
            const label = d.videoConsent ? d.videoConsent.label : "-";
            const color = st === "agreed" ? "success" : st === "disagreed" ? "danger" : st === "pending" ? "warning" : "neutral";
            return <Chip size="sm" variant="soft" color={color as any}>{label}</Chip>;
        }
        if (d.status === "completed") {
            return <span><Chip size="sm" variant="soft" color="success">서명완료</Chip>{d.roundNo === 1 && <Chip size="sm" variant="soft" color="primary" sx={{ marginLeft: "4px" }}>첫 계약</Chip>}</span>;
        }
        if (d.status === "canceled") {
            return <Chip size="sm" variant="soft" color="danger">요청취소</Chip>;
        }
        return (
            <span>
                <Chip size="sm" variant="soft" color="warning">미서명</Chip>
                <span style={{ marginLeft: "6px", fontSize: "11px", color: d.expired ? "#d32f2f" : "#6b7280" }}>{d.expired ? `${mmdd(d.expiresAt)} 만료됨` : `유효 ${mmdd(d.expiresAt)}까지`}</span>
            </span>
        );
    };

    const renderAttachments = (d: any) => {
        if (d.kind !== "contract" || d.isExcluded) return "-";
        if (d.roundNo === 1) {
            return (
                <div style={{ display: "flex", gap: "4px" }}>
                    <Button size="sm" variant="outlined" color="neutral" disabled={!d.attachments || !d.attachments.idCard}
                        onClick={() => openFileLink({ kind: "attachment", documentId: d.attachments.idCard.documentId, attachmentId: d.attachments.idCard.attachmentId })}>신분증</Button>
                    <Button size="sm" variant="outlined" color="neutral" disabled={!d.attachments || !d.attachments.bankbook}
                        onClick={() => openFileLink({ kind: "attachment", documentId: d.attachments.bankbook.documentId, attachmentId: d.attachments.bankbook.attachmentId })}>통장사본</Button>
                </div>
            );
        }
        const count = d.attachments ? d.attachments.count : 0;
        return <span className={styles.subText}>{count > 0 ? `빈 파일 ${count}건` : "-"}</span>;
    };

    //모든 줄에 [보기](현재 문서 파일) + 상태별 버튼 (완료: 감사추적 / 미서명: 재알림·취소)
    const renderDocButtons = (d: any) => {
        const view = <Button size="sm" variant="outlined" color="neutral" onClick={() => openFileLink({ kind: "file", documentId: d.documentId })}>보기</Button>;
        if (d.isExcluded) {
            return (
                <div style={{ display: "flex", gap: "4px" }} className={styles.noStrike}>
                    {view}
                    <Button size="sm" variant="plain" color="primary" loading={busyId === d.id} onClick={() => setExcluded(d, false)}>복구</Button>
                </div>
            );
        }
        if (d.status === "completed") {
            return (
                <div style={{ display: "flex", gap: "4px" }}>
                    {view}
                    {d.kind === "contract" && <Button size="sm" variant="outlined" color="neutral" onClick={() => openFileLink({ kind: "audit", documentId: d.documentId })}>감사추적</Button>}
                    {d.kind === "contract" && <Button size="sm" variant="plain" color="danger" loading={busyId === d.id} onClick={() => setExcluded(d, true)}>제외</Button>}
                </div>
            );
        }
        if (d.status === "sent" || d.status === "expired") {
            return (
                <div style={{ display: "flex", gap: "4px" }}>
                    {view}
                    <Button size="sm" variant="outlined" color={d.expired ? "warning" : "primary"} loading={busyId === d.id} onClick={() => remind(d)}>{d.expired ? "재알림 (+7일)" : "재알림"}</Button>
                    <Button size="sm" variant="outlined" color="danger" disabled={busyId === d.id} onClick={() => cancel(d)}>취소</Button>
                </div>
            );
        }
        return <div style={{ display: "flex", gap: "4px" }}>{view}</div>;
    };

    const cell = (w: number | string, children: any, extra: any = {}) => (
        <div style={{ width: typeof w === "number" ? `${w}px` : w, flexShrink: 0, ...extra }}>{children}</div>
    );

    const chip = (label: string, value: "all" | "video" | number) => (
        <Chip key={String(value)} size="sm" variant={filter === value ? "solid" : "outlined"} color="primary" onClick={() => setFilter(value)}>{label}</Chip>
    );

    return (
        <Modal open={props.open} onClose={props.onClose}>
            <ModalDialog sx={{ width: "1460px", maxWidth: "96vw", maxHeight: "90vh", overflow: "auto" }}>

                <div className={styles.modalTitle}>
                    {props.userName} — 계약서 이력
                    <span className={styles.subText} style={{ marginLeft: "10px", fontFamily: "Apple_R" }}>
                        {positions.map((p) => `${p.branchLabel ? p.branchLabel + " " : ""}${p.partName || "파트 없음"}`).join(" + ")}{positions.length > 1 ? " (겸직)" : ""}
                    </span>
                </div>

                <div style={{ display: "flex", gap: "6px", marginBottom: "10px", flexWrap: "wrap" }}>
                    {chip("전체", "all")}
                    {positions.map((p) => chip(p.partName || `직무 ${p.id}`, p.id))}
                    {chip("영상동의서", "video")}
                </div>

                {loading
                    ?
                    <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}><CircularProgress /></div>
                    :
                    <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "auto" }}>
                        <div style={{ display: "flex", gap: "10px", padding: "8px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "12px", color: "#888", minWidth: "1380px" }}>
                            {cell(40, "회차")}{cell(100, "종류")}{cell(140, "파트")}{cell(165, "계약기간")}{cell(100, "발송")}{cell(95, "서명 완료")}{cell(170, "상태")}{cell(55, "재알림")}{cell(150, "첨부파일")}{cell(330, "문서")}
                        </div>
                        {filtered.map((d) => (
                            <div key={d.id} className={d.isExcluded ? styles.excluded : undefined} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", borderBottom: "1px solid #f0f0f0", fontSize: "13px", fontFamily: "Apple_R", background: d.kind === "videoConsent" ? "#fafafa" : "white", minWidth: "1380px" }}>
                                {cell(40, d.kind === "contract" && !d.isExcluded ? d.roundNo : "-")}
                                {cell(100, d.kind === "contract" ? "근로계약서" : "영상정보 동의서")}
                                {cell(140, d.kind === "contract" ? <span title={d.partName} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.partName || "-"}</span> : <span className={styles.subText}>(사람 단위)</span>)}
                                {cell(165, d.kind === "contract" && d.periodStart ? `${d.periodStart} ~ ${d.periodEnd}` : "-")}
                                {cell(100, <span className={styles.subText} style={{ fontSize: "12px" }}>{d.sentByName} · {mmdd(d.sentAt)}</span>)}
                                {cell(95, d.completedAt ? mmddHHmm(d.completedAt) : "-")}
                                {cell(170, renderStatus(d))}
                                {cell(55, (
                                    <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{(d.remindLog || []).length ? d.remindLog.map((l: any) => `${mmdd(l.at)}${l.extended ? " (+7일)" : ""}`).join("\n") : "재알림 기록 없음"}</div>} placement="top">
                                        <span>{d.remindCount}회</span>
                                    </Tooltip>
                                ))}
                                {cell(150, renderAttachments(d))}
                                {cell(330, renderDocButtons(d))}
                            </div>
                        ))}
                        {filtered.length === 0 && <div style={{ padding: "20px", color: "#9ca3af", fontFamily: "Apple_R" }}>문서가 없습니다</div>}
                    </div>
                }

                <div className={styles.modalButtons}>
                    <div className={styles.hint}>회차는 파트별로 셈 · <b>제외</b> = 잘못 보내 서명된 문서를 회차에서 뺌(뒤 회차가 당겨지고 이 줄은 취소선으로 남음, 복구 가능) · 첫 계약서의 첨부만 신분증·통장사본으로 저장(그 다음 회차 첨부는 빈 파일이라 무시) · PDF·첨부는 유캔싸인에서 그때그때 받아옴(링크 3분 유효)</div>
                    <Button variant="plain" color="neutral" onClick={props.onClose}>닫기</Button>
                </div>

            </ModalDialog>
        </Modal>
    );
};

export default ContractHistoryModal;
